import { makeValuesPredicate } from '@sgme/fp';
import type { FormError } from 'models/formError';
import { ExposedFormStatus, FormStatus } from 'state/forms/models';

export const isBatchFormLoadingByStatus = makeValuesPredicate(['INITIALIZING', 'REPRICING', 'RELOADING']);

export const isBatchFormActionFailedByStatus = makeValuesPredicate([
  'INITIALIZATION_ERROR',
  'FETCH_ERROR',
  'REPRICE_ERROR',
  'PATCH_ERROR',
  'DUPLICATION_ERROR',
]);

export const isBatchFormActionFailed = makeValuesPredicate([
  'INITIALIZATION_ERROR',
  'FETCH_ERROR',
  'REPRICE_ERROR',
  'PATCH_ERROR',
  'DUPLICATION_ERROR',
]);

export const isBatchFormFailedActionRetryable = makeValuesPredicate(['INITIALIZATION_ERROR', 'PATCH_ERROR']);

export const canBatchFormBeExportedByStatus = makeValuesPredicate(['REJECTED', 'QUOTED', 'QUOTED_EXPIRED']);

export const canBatchFormBeDeletedByStatus = makeValuesPredicate([
  'READY',
  'REJECTED',
  'UNKNOWN_ERROR',
  'NOT_VALID',
  'QUOTED',
  'QUOTED_EXPIRED',
]);

export const canBatchFormBeOpenedInQuoteWorkspaceByStatus = makeValuesPredicate([
  'QUOTED',
  'QUOTED_EXPIRED',
  'REJECTED',
]);
export const canBatchFormBePricedByStatus = makeValuesPredicate([
  'READY',
  'QUOTED',
  'QUOTED_EXPIRED',
  'REJECTED',
  'UNKNOWN_ERROR',
]);

const batchFormCannotBeEdited = makeValuesPredicate(['QUOTING', 'DUPLICATING']);
export const canBatchFormBeEditedByStatus = (formStatus: FormStatus) => !batchFormCannotBeEdited(formStatus);

export const canBatchFormBeClonedByStatus = makeValuesPredicate([
  'READY',
  'REJECTED',
  'UNKNOWN_ERROR',
  'NOT_VALID',
  'QUOTED',
  'QUOTED_EXPIRED',
]);

export const canBatchFormDatesBeRefreshedByStatus = makeValuesPredicate([
  'READY',
  'NOT_VALID',
  'REJECTED',
  'QUOTED',
  'QUOTED_EXPIRED',
  'UNKNOWN_ERROR',
]);

export function canBatchFormBeAbortedByStatus(formStatus: FormStatus): boolean {
  return formStatus === 'QUOTING';
}

export function isNbOfBatchFormWithinThreshold(nbOfForms: number): boolean {
  return nbOfForms <= (window?.sgmeConfiguration?.maxNumberOfFormsPerBatchTab || 100);
}

export function translateFormStatus(formStatus: FormStatus): ExposedFormStatus {
  switch (formStatus) {
    case 'DUPLICATING':
      return 'Duplicating';
    case 'PATCHING':
      return 'Patching';
    case 'VALIDATING':
      return 'Validating';
    case 'QUOTING':
      return 'InProgress';
    case 'READY':
      return 'Ready';
    case 'REJECTED':
      return 'Rejected';
    case 'QUOTED':
      return 'Success';
    case 'QUOTED_EXPIRED':
      return 'Expired';
    case 'UNKNOWN_ERROR':
      return 'UnknownError';
    case 'INITIALIZATION_ERROR':
    case 'FETCH_ERROR':
    case 'REPRICE_ERROR':
    case 'PATCH_ERROR':
    case 'DUPLICATION_ERROR':
      return 'ActionFailed';
    case 'INITIALIZING':
    case 'REPRICING':
    case 'RELOADING':
      return 'Loading';
    default:
      return 'NotReady';
  }
}

export function hasErrorSeverity(validationErrors?: FormError[]) {
  return validationErrors ? validationErrors?.some(v => v.severity === 'Error') : false;
}
